import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rupiah, tgl, today } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Hutang() {
  const [form, setForm] = useState({ keterangan: "", jumlah: "", metode: "tunai", tanggal: today() });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => api.get("/debts").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/debts", { ...form, jumlah: Number(form.jumlah) });
      toast.success("Hutang tercatat, dana masuk ke " + (form.metode === "transfer" ? "Bank" : "Kas"));
      setForm({ keterangan: "", jumlah: "", metode: "tunai", tanggal: today() });
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal mencatat hutang"));
    } finally {
      setLoading(false);
    }
  };

  const bayar = async (id) => {
    try {
      await api.post(`/debts/${id}/bayar`);
      toast.success("Hutang dibayar, saldo berkurang");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal membayar hutang"));
    }
  };

  const hapus = async (id) => {
    await api.delete(`/debts/${id}`);
    toast.success("Catatan hutang dihapus");
    load();
  };

  const belumLunas = items.filter((d) => d.status !== "lunas");
  const total = belumLunas.reduce((s, d) => s + d.jumlah, 0);

  return (
    <div data-testid="hutang-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Liabilitas</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Hutang</h1>

      <div className="mt-4 rounded-md border border-border bg-white p-4" data-testid="hutang-total-card">
        <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">Total Hutang Belum Lunas</p>
        <p className="mt-1 text-xl font-bold text-red-600" data-testid="hutang-total">{rupiah(total)}</p>
      </div>

      <form onSubmit={submit} className="mt-4 rounded-md border border-border bg-white p-5 space-y-4" data-testid="hutang-form">
        <div className="space-y-1.5">
          <Label>Keterangan</Label>
          <Input required value={form.keterangan} onChange={(e) => setForm((f) => ({ ...f, keterangan: e.target.value }))} placeholder="cth: Pinjaman modal dari supplier" data-testid="hutang-keterangan-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Jumlah (Rp)</Label>
            <Input required type="number" min="0" value={form.jumlah} onChange={(e) => setForm((f) => ({ ...f, jumlah: e.target.value }))} data-testid="hutang-jumlah-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal</Label>
            <Input type="date" value={form.tanggal} onChange={(e) => setForm((f) => ({ ...f, tanggal: e.target.value }))} data-testid="hutang-tanggal-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Dana Masuk Ke</Label>
          <div className="grid grid-cols-2 gap-2">
            {[["tunai", "Kas (Tunai)"], ["transfer", "Bank (Transfer)"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, metode: v }))} data-testid={`hutang-metode-${v}`}
                className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-150 active:scale-[0.98] ${form.metode === v ? "border-primary bg-primary text-white" : "border-border bg-white"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <Button type="submit" className="w-full rounded-full active:scale-95 transition-transform duration-100" disabled={loading} data-testid="hutang-submit-button">
          {loading ? "Menyimpan..." : "Catat Hutang"}
        </Button>
      </form>

      <div className="mt-6 rounded-md border border-border bg-white divide-y divide-border" data-testid="hutang-list">
        {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Belum ada hutang tercatat.</p>}
        {items.map((d) => (
          <div key={d.id} className="flex items-center justify-between p-3.5" data-testid={`hutang-item-${d.id}`}>
            <div>
              <p className="text-sm font-semibold">{d.keterangan}</p>
              <p className="text-xs text-muted-foreground">{tgl(d.tanggal)} · masuk ke {d.metode === "transfer" ? "Bank" : "Kas"} · {d.status === "lunas" ? `Lunas ${tgl(d.lunas_at)}` : "Belum Lunas"}</p>
            </div>
            <div className="flex items-center gap-2">
              <p className={`text-sm font-bold ${d.status === "lunas" ? "text-muted-foreground" : "text-red-600"}`}>{rupiah(d.jumlah)}</p>
              {d.status !== "lunas" && (
                <button onClick={() => bayar(d.id)} data-testid={`hutang-bayar-${d.id}`} className="rounded-md border border-emerald-600 px-2.5 py-1 text-xs font-bold text-emerald-600 hover:bg-emerald-50 transition-colors duration-150 active:scale-95">Bayar</button>
              )}
              <button onClick={() => hapus(d.id)} data-testid={`hutang-delete-${d.id}`} className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150 active:scale-95">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
